/**
 * Camera coverage (round 3): which camera has — or will have — the aircraft in frame, now and across
 * the whole mission. Pure; used by the live Coverage panel, the map's "active" cones and Replay's
 * coverage lanes / debrief summary.
 *
 * Mount semantics (CameraMount):
 *  - 'static' / 'manual' (Static): a fixed wedge along its heading. In frame when the aircraft is
 *    inside the horizontal FOV and big enough to matter (fill ≥ minFillPct); otherwise "out of frame"
 *    with how far outside the frame edge it is (turnDeg, signed: + = right of the view).
 *  - 'crew' (Manned): with a live aim (round 4 — the operator's phone facing, or a fresh camera
 *    heading; see cameraStatesAt) it is judged like a Static wedge along that aim. Without one (stale
 *    > 10 s, inaccurate > 25°, never sent) the operator is assumed to point at the aircraft, so it
 *    "can cover" whenever the camera has a position and the aircraft is within useful range for its
 *    lens (fill ≥ minFillPct, else "too far") — and the words say "aim unknown".
 *
 * Lenses (round 4): a prime has one focal; a zoom (focalMinMm–focalMaxMm) is in frame when the
 * aircraft fits somewhere in the range — inside the WIDE end's field of view and big enough at the
 * LONG end — and reports the focal that frames it well (≈ FRAME_TARGET_FILL_PCT, kept inside the
 * frame): "fills 10% at 200 mm".
 *  - 'aircraft' (Aerial): on another aircraft; heading unknown → in frame when within useful range.
 *    A camera on the tracked aircraft itself is 'unknown' (it films from it, not at it).
 *
 * Informational only: "in frame" is geometry from public ADS-B positions, not a promise of a shot.
 */
import type { Aircraft, AircraftState, Camera, CameraState, CrewState, EpochMs, Id, LatLon, MissionRecord, WorldState } from './types.ts';
import { angleDiffDeg, bearingDeg, destinationPoint, distanceM, M_PER_FT, M_PER_SM, MPS_PER_KT } from './geo.ts';
import { focalRange, fovDeg, isZoom } from './fov.ts';
import { HERON_SPAN_M } from './framing.ts';
import { aircraftStateAt, baselineAltitudeFt, cameraStatesAt, crewStateAt, lastIndexAtOrBefore, phaseForState } from './world.ts';
import { isAirbornePhase } from './phase.ts';

export type CoverageState = 'in_frame' | 'out_of_frame' | 'too_far' | 'blocked' | 'no_position' | 'unknown';
export type CoverageKind = 'static' | 'manned' | 'aerial';

export interface CameraCoverage {
  cameraId: Id;
  state: CoverageState;
  /** Slant range when the height is known, else ground range (m). */
  rangeM: number | null;
  /** % of frame width the aircraft spans with the camera's current lens. */
  fillPct: number | null;
  /** Static cams out of frame: degrees outside the frame edge (signed, + = right of its view). */
  turnDeg: number | null;
  kind: CoverageKind;
  /** Bearing camera → aircraft (°T) — where a Manned/Aerial camera points ("can cover"). */
  aimDeg: number | null;
  /** true for Aerial and for a Manned camera without a live aim: the operator is assumed to point at the aircraft. */
  assumedAim: boolean;
  /** No focal length / sensor on the camera: DEFAULT_LENS was used for fill and FOV. */
  defaultLens: boolean;
  /** The focal length (mm) fillPct refers to: the prime, or for a zoom the focal that frames it well. */
  focalMm?: number | null;
  /** The camera has a zoom range (fillPct is at focalMm, chosen inside the range). */
  zoom?: boolean;
  /** Manned: 'live' = aimed by the operator's phone / a camera heading, 'unknown' = assumed ("aim unknown"). */
  aim?: 'live' | 'unknown';
  /** A retained last position is not evidence of present camera coverage. */
  positionIssue?: 'aircraft' | 'camera';
}

/** True/false only when current geometry and aim establish an answer; null means unknown. */
export function coveragePresence(c: CameraCoverage | null | undefined): boolean | null {
  if (!c || c.state === 'unknown' || c.state === 'no_position' || c.positionIssue) return null;
  if (c.state === 'in_frame' && c.assumedAim) return null;
  return c.state === 'in_frame';
}

export function coverageCounts(cameras: readonly CameraCoverage[]) {
  return {
    inFrame: cameras.filter(c => coveragePresence(c) === true).length,
    potential: cameras.filter(c => c.state === 'in_frame' && c.assumedAim && !c.positionIssue).length,
    unknown: cameras.filter(c => c.state === 'unknown' || c.state === 'no_position' || c.positionIssue).length,
  };
}

export function coverageCountText(cameras: readonly CameraCoverage[]): string {
  const n = coverageCounts(cameras);
  if (n.inFrame) return `${n.inFrame} in frame${n.potential ? ` · ${n.potential} may cover` : ''}${n.unknown ? ` · ${n.unknown} unknown` : ''}`;
  if (n.potential) return `${n.potential} may cover · aim unknown${n.unknown ? ` · ${n.unknown} unknown` : ''}`;
  return n.unknown ? 'Coverage unknown' : 'No camera in frame';
}

function usablePosition(p: {lat:number;lon:number;freshness:AircraftState['freshness']} | null | undefined): boolean {
  return !!p && (p.freshness === 'live' || p.freshness === 'aging') &&
    Number.isFinite(p.lat) && Math.abs(p.lat) <= 90 && Number.isFinite(p.lon) && Math.abs(p.lon) <= 180;
}

/** Lens assumed when a camera has no focal length / sensor (a normal lens on full frame). */
export const DEFAULT_LENS = Object.freeze({ focalLengthMm: 50, sensorWidthMm: 36, sensorHeightMm: 24 });
/** Below this frame fill the aircraft is a speck: "too far" (≈ 2 %, contract). */
export const COVERAGE_MIN_FILL_PCT = 2;
/** Timeline sampling step (s). */
export const COVERAGE_STEP_SEC = 2;
/** "Next in frame" look-ahead (s). */
export const COVERAGE_PREDICT_SEC = 120;
/** A zoom reports the focal that makes the aircraft fill about this much of the frame width (%). */
export const FRAME_TARGET_FILL_PCT = 33;

export interface CoverageOptions {
  /** Minimum frame fill (%) that counts as in frame. Default COVERAGE_MIN_FILL_PCT. */
  minFillPct?: number;
  /** Aircraft span (m) for fill; default the aircraft's wingspan, else HERON_SPAN_M. */
  spanM?: number;
  /**
   * Optional terrain line of sight camera → aircraft. false = blocked, true = visible, null = unknown.
   * Only consulted when the camera would otherwise have the aircraft in frame.
   */
  sight?: (cameraId: Id, target: LatLon & { altM: number | null }) => boolean | null;
  /** Changes whenever `sight` would answer differently (e.g. terrain profiles loaded) — part of the timeline memo key. */
  sightKey?: string;
  /**
   * Aircraft height above the cameras (ft) when the state carries no AGL — e.g. altitude minus the
   * aircraft's ground baseline. Null/undefined = use ground range only.
   */
  heightFt?: number | null;
}

type CamLike = Pick<Camera, 'id' | 'mount' | 'aircraftId' | 'focalLengthMm' | 'sensorWidthMm' | 'sensorHeightMm' | 'focalMinMm' | 'focalMaxMm'>;
type BundleLike = { cameras: readonly CamLike[]; aircraft: ReadonlyArray<Pick<Aircraft, 'id' | 'wingspanM'>> };

export function coverageKind(mount: Camera['mount'] | null | undefined): CoverageKind {
  return mount === 'crew' ? 'manned' : mount === 'aircraft' ? 'aerial' : 'static';
}

interface Lens {
  /** Wide and long ends (equal for a prime). */
  minMm: number;
  maxMm: number;
  sensorWmm: number;
  /** Horizontal FOV at the wide end — the widest the camera can see. */
  hfovDeg: number;
  zoom: boolean;
  isDefault: boolean;
}

function lensFor(cam: CamLike, cs: Pick<CameraState, 'focalLengthMm'> | null): Lens {
  const w = cam.sensorWidthMm;
  const zr = isZoom(cam) ? focalRange(cam) : null;
  if (zr && w != null && w > 0) return { minMm: zr.minMm, maxMm: zr.maxMm, sensorWmm: w, hfovDeg: fovDeg(w, zr.minMm), zoom: true, isDefault: false };
  const f = zr ? null : (cs?.focalLengthMm ?? cam.focalLengthMm);
  if (f != null && f > 0 && w != null && w > 0) return { minMm: f, maxMm: f, sensorWmm: w, hfovDeg: fovDeg(w, f), zoom: false, isDefault: false };
  const sw = w != null && w > 0 ? w : DEFAULT_LENS.sensorWidthMm;
  if (zr) return { minMm: zr.minMm, maxMm: zr.maxMm, sensorWmm: sw, hfovDeg: fovDeg(sw, zr.minMm), zoom: true, isDefault: true };
  const focal = f != null && f > 0 ? f : DEFAULT_LENS.focalLengthMm;
  return { minMm: focal, maxMm: focal, sensorWmm: sw, hfovDeg: fovDeg(sw, focal), zoom: false, isDefault: true };
}

/** % of frame width filled by `spanM` at `rangeM` (rectilinear) at `focalMm`. */
function fillFor(spanM: number, rangeM: number, focalMm: number, sensorWmm: number): number {
  if (!(rangeM > 0)) return Infinity;
  return (spanM * focalMm * 100) / (sensorWmm * rangeM);
}

/**
 * The focal a zoom should use: the one that fills ≈ FRAME_TARGET_FILL_PCT, no longer than keeps an
 * off-centre aircraft inside the frame (`offDeg` from the aim), clamped to the range.
 */
function framingFocal(lens: Lens, spanM: number, rangeM: number, offDeg: number | null): number {
  if (!lens.zoom || !(rangeM > 0)) return lens.maxMm;
  let f = (FRAME_TARGET_FILL_PCT * lens.sensorWmm * rangeM) / (spanM * 100);
  const off = offDeg == null ? 0 : Math.abs(offDeg);
  if (off > 0.5) f = Math.min(f, lens.sensorWmm / (2 * Math.tan(Math.min(89, off / 0.9) * (Math.PI / 180))));
  return Math.min(lens.maxMm, Math.max(lens.minMm, f));
}

interface Target {
  pos: LatLon;
  /** MSL ft (for sight + aerial). */
  altFt: number | null;
  /** Height above the cameras (ft), when known. */
  heightFt: number | null;
}

/** Core evaluator for one camera against one target position. */
function evalCamera(
  cam: CamLike,
  cs: CameraState | null,
  target: Target,
  targetAircraftId: Id,
  hostState: AircraftState | null,
  spanM: number,
  minFill: number,
  sight: CoverageOptions['sight'],
): CameraCoverage {
  const kind = coverageKind(cam.mount);
  const lens = lensFor(cam, cs);
  // a manned camera with a live aim is judged along it, like a static wedge
  const liveAim = kind === 'manned' && cs?.aim === 'live' && cs.headingDeg != null;
  const assumedAim = kind === 'aerial' || (kind === 'manned' && !liveAim);
  const base: CameraCoverage = {
    cameraId: cam.id, state: 'no_position', rangeM: null, fillPct: null, turnDeg: null, kind, aimDeg: null,
    assumedAim, defaultLens: lens.isDefault, focalMm: lens.zoom ? null : lens.maxMm, zoom: lens.zoom,
  };
  if (kind === 'manned') base.aim = liveAim ? 'live' : 'unknown';
  if (kind === 'aerial' && cam.aircraftId === targetAircraftId) return { ...base, state: 'unknown' };
  const pos = cs?.position ?? null;
  if (!pos) return base;
  if (!usablePosition({ ...pos, freshness: cs!.freshness })) return { ...base, positionIssue: 'camera' };
  const ground = distanceM(pos, target.pos);
  let vertM: number | null = null;
  if (kind === 'aerial') {
    if (hostState?.altFt != null && target.altFt != null) vertM = (target.altFt - hostState.altFt) * M_PER_FT;
  } else if (target.heightFt != null) vertM = Math.max(0, target.heightFt) * M_PER_FT;
  const rangeM = vertM != null ? Math.hypot(ground, vertM) : ground;
  const brg = ground > 0 ? bearingDeg(pos, target.pos) : 0;
  const out: CameraCoverage = { ...base, rangeM, aimDeg: brg };

  let off: number | null = null;
  if (kind === 'static' || liveAim) {
    const heading = cs?.headingDeg ?? null;
    if (heading == null) return { ...out, state: 'unknown', aimDeg: null };
    out.aimDeg = liveAim ? heading : null;
    off = ground > 0 ? angleDiffDeg(heading, brg) : 0;
  }
  // the long end decides "too far"; fill is reported at the focal that frames it well
  const focal = framingFocal(lens, spanM, rangeM, off);
  const fillTele = fillFor(spanM, rangeM, lens.maxMm, lens.sensorWmm);
  const fill = fillFor(spanM, rangeM, focal, lens.sensorWmm);
  out.fillPct = Number.isFinite(fill) ? fill : 100;
  out.focalMm = focal;
  if (off != null) {
    const half = lens.hfovDeg / 2;
    if (Math.abs(off) > half) return { ...out, state: 'out_of_frame', turnDeg: Math.sign(off) * (Math.abs(off) - half) };
  }
  if (fillTele < minFill) return { ...out, state: 'too_far' };
  if (sight) {
    const v = sight(cam.id, { lat: target.pos.lat, lon: target.pos.lon, altM: target.altFt != null ? target.altFt * M_PER_FT : null });
    if (v === false) return { ...out, state: 'blocked' };
  }
  return { ...out, state: 'in_frame' };
}

/** No aircraft position: every camera reads 'unknown' (lens / aim facts kept for the UI). */
function unknownCoverage(cam: CamLike, cs: CameraState | null, positionIssue?: CameraCoverage['positionIssue']): CameraCoverage {
  const lens = lensFor(cam, cs);
  const kind = coverageKind(cam.mount);
  const liveAim = kind === 'manned' && cs?.aim === 'live';
  const c: CameraCoverage = {
    cameraId: cam.id, state: 'unknown', rangeM: null, fillPct: null, turnDeg: null, kind, aimDeg: null,
    assumedAim: kind === 'aerial' || (kind === 'manned' && !liveAim), defaultLens: lens.isDefault, focalMm: lens.zoom ? null : lens.maxMm, zoom: lens.zoom,
  };
  if (kind === 'manned') c.aim = liveAim ? 'live' : 'unknown';
  if (positionIssue) c.positionIssue = positionIssue;
  return c;
}

function spanFor(bundle: BundleLike, aircraftId: Id, opts?: CoverageOptions): number {
  if (opts?.spanM && opts.spanM > 0) return opts.spanM;
  const ws = bundle.aircraft.find((a) => a.id === aircraftId)?.wingspanM;
  return ws && ws > 0 ? ws : HERON_SPAN_M;
}

function targetOf(s: AircraftState, heightFt: number | null | undefined): Target {
  const h = s.aglFt != null ? s.aglFt : heightFt != null ? heightFt : null;
  return { pos: { lat: s.lat, lon: s.lon }, altFt: s.altFt, heightFt: h };
}

/**
 * Coverage of `aircraftId` by every camera in `bundle` at the world's instant. Cameras follow
 * bundle order. When the aircraft has no state every camera reads 'unknown'.
 */
export function coverageAt(bundle: BundleLike, world: Pick<WorldState, 'aircraft' | 'cameras'>, aircraftId: Id, opts?: CoverageOptions): CameraCoverage[] {
  const s = world.aircraft.find((a) => a.aircraftId === aircraftId) ?? null;
  const span = spanFor(bundle, aircraftId, opts);
  const minFill = opts?.minFillPct ?? COVERAGE_MIN_FILL_PCT;
  const states = new Map(world.cameras.map((c) => [c.cameraId, c]));
  return bundle.cameras.map((cam) => {
    if (!usablePosition(s)) return unknownCoverage(cam, states.get(cam.id) ?? null, 'aircraft');
    const host = cam.mount === 'aircraft' && cam.aircraftId ? (world.aircraft.find((a) => a.aircraftId === cam.aircraftId) ?? null) : null;
    return evalCamera(cam, states.get(cam.id) ?? null, targetOf(s!, opts?.heightFt), aircraftId, host, span, minFill, opts?.sight);
  });
}

export interface NextInFrame {
  cameraId: Id;
  /** Seconds from now (straight-line projection). */
  inSec: number;
}

/**
 * The first camera (not in frame now) that the aircraft enters, projecting it along its current
 * track / speed / climb for up to `horizonSec` (default 2 min). Null when nothing is predictable
 * (no speed/track, or no camera picks it up within the horizon).
 */
export function nextInFrame(
  bundle: BundleLike,
  world: Pick<WorldState, 'aircraft' | 'cameras'>,
  aircraftId: Id,
  opts?: CoverageOptions & { horizonSec?: number; stepSec?: number },
): NextInFrame | null {
  const s = world.aircraft.find((a) => a.aircraftId === aircraftId);
  if (!s || !usablePosition(s) || s.gsKt == null || s.trackDeg == null || !(s.gsKt > 5)) return null;
  const horizon = opts?.horizonSec ?? COVERAGE_PREDICT_SEC;
  const step = opts?.stepSec ?? COVERAGE_STEP_SEC;
  const span = spanFor(bundle, aircraftId, opts);
  const minFill = opts?.minFillPct ?? COVERAGE_MIN_FILL_PCT;
  const now = coverageAt(bundle, world, aircraftId, opts);
  const candidates = bundle.cameras.filter((c, i) => now[i]!.state !== 'in_frame' && now[i]!.state !== 'no_position' && now[i]!.state !== 'unknown');
  if (!candidates.length) return null;
  const states = new Map(world.cameras.map((c) => [c.cameraId, c]));
  const t0 = targetOf(s, opts?.heightFt);
  const v = s.gsKt * MPS_PER_KT;
  const vsFtS = (s.vsFpm ?? 0) / 60;
  for (let dt = step; dt <= horizon + 1e-9; dt += step) {
    const pos = destinationPoint(t0.pos, s.trackDeg, v * dt);
    const tgt: Target = {
      pos,
      altFt: t0.altFt != null ? t0.altFt + vsFtS * dt : null,
      heightFt: t0.heightFt != null ? Math.max(0, t0.heightFt + vsFtS * dt) : null,
    };
    for (const cam of candidates) {
      const host = cam.mount === 'aircraft' && cam.aircraftId ? (world.aircraft.find((a) => a.aircraftId === cam.aircraftId) ?? null) : null;
      // the projection holds other aircraft where they are (good enough for two minutes of "next")
      const c = evalCamera(cam, states.get(cam.id) ?? null, tgt, aircraftId, host, span, minFill, opts?.sight);
      if (c.state === 'in_frame') return { cameraId: cam.id, inSec: dt };
    }
  }
  return null;
}

// ───────────── plain words ─────────────

/** Statute miles for the crew: "0.4 mi", "12 mi"; under ~0.1 mi in feet ("450 ft"). */
export function milesText(m: number | null): string {
  if (m == null || !Number.isFinite(m)) return '—';
  const mi = m / M_PER_SM;
  if (mi < 0.1) return `${Math.max(10, Math.round(m / M_PER_FT / 10) * 10)} ft`;
  return mi < 10 ? `${mi.toFixed(1)} mi` : `${Math.round(mi)} mi`;
}

/** "8%" / "<1%" / ">100%". */
export function fillText(p: number | null): string {
  if (p == null || !Number.isFinite(p)) return '—';
  if (p >= 100) return '>100%';
  if (p < 1) return '<1%';
  return `${Math.round(p)}%`;
}

export interface CoverageWords {
  /** Short headline: "In frame", "Can cover", "Out of frame", "Too far", "Behind terrain", "No position", "—". */
  head: string;
  /** Detail after the headline: "1.2 mi · fills 8%", "40° right of its view". */
  detail: string;
  /** True only for current in-frame geometry with known aim. */
  ok: boolean;
}

/** Plain-language row text for one camera's coverage (contract round 3 wording). */
export function coverageWords(c: CameraCoverage): CoverageWords {
  if (c.positionIssue) return { head: 'Coverage unknown', detail: c.positionIssue === 'aircraft' ? 'Aircraft position missing or out of date' : 'Camera/operator position out of date', ok: false };
  const at = c.zoom && c.focalMm != null && c.fillPct != null ? ` at ${Math.round(c.focalMm)} mm` : '';
  const rf = `${milesText(c.rangeM)} · fills ${fillText(c.fillPct)}${at}`;
  const aimUnknown = c.kind === 'manned' && c.aim === 'unknown' ? ' · aim unknown' : '';
  const view = c.kind === 'manned' ? 'where they aim' : 'its view';
  switch (c.state) {
    case 'in_frame':
      return { head: c.assumedAim ? 'Can cover' : 'In frame', detail: rf + aimUnknown, ok: !c.assumedAim };
    case 'out_of_frame': {
      const deg = c.turnDeg == null ? null : Math.max(1, Math.round(Math.abs(c.turnDeg)));
      const side = c.turnDeg != null && c.turnDeg < 0 ? 'left' : 'right';
      return { head: 'Out of frame', detail: deg == null ? milesText(c.rangeM) : `${deg}° ${side} of ${view}`, ok: false };
    }
    case 'too_far':
      return { head: 'Too far', detail: rf + aimUnknown, ok: false };
    case 'blocked':
      return { head: 'Behind terrain', detail: milesText(c.rangeM), ok: false };
    case 'no_position':
      return { head: 'No position', detail: c.kind === 'manned' ? 'operator not sharing, no placed spot' : 'not placed', ok: false };
    default:
      return { head: '—', detail: c.kind === 'static' ? 'no heading set' : c.kind === 'aerial' ? 'on this aircraft' : 'no aircraft position', ok: false };
  }
}

// ───────────── timeline (Replay) ─────────────

export interface CoverageSegment {
  from: EpochMs;
  to: EpochMs;
  state: CoverageState;
  /** Largest fill in the segment and when (best moment). */
  bestFillPct: number | null;
  bestT: EpochMs | null;
  /** Closest range in the segment (m). */
  minRangeM: number | null;
}

export interface CameraTimeline {
  cameraId: Id;
  segments: CoverageSegment[];
  /**
   * Closest framings while in frame: samples whose fill is the largest within ±PEAK_WINDOW_SEC (one
   * per pass), oldest first — candidates for "best moments".
   */
  peaks: Array<{ t: EpochMs; fillPct: number }>;
}

/** Half-window (s) for a fill peak to count as a pass's closest framing. */
export const PEAK_WINDOW_SEC = 30;

export interface Span {
  from: EpochMs;
  to: EpochMs;
}

export interface CoverageSampling {
  aircraftId: Id;
  from: EpochMs;
  to: EpochMs;
  stepSec: number;
  cameras: CameraTimeline[];
  /** Stretches where the aircraft was in the air (inferred flight phase). */
  airborne: Span[];
  /** Airborne stretches where no camera had it in frame. */
  nobody: Span[];
  /** Airborne continuity after fresh evidence, bounded by recent cadence. */
  inferredAirborne: Span[];
  /** Missing or unclassifiable telemetry beyond the continuity bound. */
  unknown: Span[];
}

export interface CoverageTimelineOptions extends Omit<CoverageOptions, 'heightFt'> {
  stepSec?: number;
}

const timelineCache = new WeakMap<MissionRecord, Map<string, CoverageSampling>>();

/**
 * Sample the record every `stepSec` (default 2 s) over [from, to] with the same reconstruction as
 * Replay (aircraftStateAt / crewStateAt / cameraStatesAt) and run-length encode each camera's
 * coverage. Memoised per record identity + arguments (a live record is a new object per update).
 */
export function coverageSampling(record: MissionRecord, aircraftId: Id, from: EpochMs, to: EpochMs, opts?: CoverageTimelineOptions): CoverageSampling {
  const step = Math.max(0.5, opts?.stepSec ?? COVERAGE_STEP_SEC);
  const minFill = opts?.minFillPct ?? COVERAGE_MIN_FILL_PCT;
  const key = `${aircraftId}|${Math.round(from)}|${Math.round(to)}|${step}|${minFill}|${opts?.spanM ?? ''}|${opts?.sight ? `s${opts.sightKey ?? ''}` : ''}`;
  let byKey = timelineCache.get(record);
  const hit = byKey?.get(key);
  if (hit) return hit;

  const span = spanFor(record, aircraftId, opts);
  const obs = record.aircraftObs[aircraftId] ?? [];
  const noTrail = { trailSec: 0 };
  const opIds = new Set<Id>();
  const hostIds = new Set<Id>();
  for (const c of record.cameras) {
    if (c.mount === 'crew' && c.operatorUserId) opIds.add(c.operatorUserId);
    if (c.mount === 'aircraft' && c.aircraftId && c.aircraftId !== aircraftId) hostIds.add(c.aircraftId);
  }
  const runs: CameraTimeline[] = record.cameras.map((c) => ({ cameraId: c.id, segments: [], peaks: [] }));
  const airborne: Span[] = [];
  const nobody: Span[] = [];
  const unknown: Span[] = [];
  const inferredAirborne: Span[] = [];
  const stepMs = step * 1000;
  const n = Math.max(1, Math.floor((to - from) / stepMs) + 1);
  // in-frame fill per camera per sample (NaN = not in frame) for the pass peaks
  const fills = record.cameras.map(() => new Float32Array(n).fill(NaN));
  const times = new Float64Array(n);
  let used = 0;
  let lastPhaseIdx = -2;
  let lastAirborne = false;

  const extend = (list: Span[], t: EpochMs, on: boolean, end: EpochMs) => {
    const last = list[list.length - 1];
    if (on) {
      if (last && last.to === t) last.to = end;
      else list.push({ from: t, to: end });
    }
  };

  for (let k = 0; k < n; k++) {
    const t = Math.min(to, from + k * stepMs);
    const end = Math.min(to, t + stepMs);
    const raw = obs.length ? aircraftStateAt(aircraftId, obs, t, noTrail) : null;
    // Flight continuity and camera position validity are separate evidence questions.
    const s = raw && (raw.freshness === 'live' || raw.freshness === 'aging') ? raw : null;
    // flight phase only changes at samples: recompute when a new observation is in force
    let air = false;
    const idx = lastIndexAtOrBefore(obs, t);
    if (raw) {
      if (idx !== lastPhaseIdx) {
        lastPhaseIdx = idx;
        lastAirborne = isAirbornePhase(phaseForState(aircraftStateAt(aircraftId, obs, obs[idx]!.t, noTrail)!, obs, { airports: record.airports }).phase);
      }
      // Median of up to five preceding intervals tolerates missed reports without letting
      // a single hours-long dropout establish a new cadence. 60 s floor, 5 min ceiling.
      const intervals: number[] = [];
      for (let j = Math.max(1, idx - 4); j <= idx; j++) {
        const dt = obs[j]!.t - obs[j - 1]!.t;
        if (dt > 0) intervals.push(dt);
      }
      intervals.sort((a, b) => a - b);
      const cadence = intervals.length ? intervals[Math.floor((intervals.length - 1) / 2)]! : 0;
      const continuityMs = Math.min(300000, Math.max(60000, cadence * 3));
      air = lastAirborne && t - obs[idx]!.t < continuityMs;
    }
    const aircraft: AircraftState[] = s ? [s] : [];
    for (const id of hostIds) {
      const hs = aircraftStateAt(id, record.aircraftObs[id] ?? [], t, noTrail);
      if (hs) aircraft.push(hs);
    }
    const crew: CrewState[] = [];
    for (const uid of opIds) {
      const cs = crewStateAt(uid, record.crewObs[uid] ?? [], t, noTrail);
      if (cs) crew.push(cs);
    }
    const camStates = cameraStatesAt(record, t, aircraft, crew);
    const base = s ? baselineAltitudeFt(obs, t) : null;
    const heightFt = s && s.altFt != null && base != null ? s.altFt - base : null;
    let anyIn = false;
    for (let i = 0; i < record.cameras.length; i++) {
      const cam = record.cameras[i]!;
      let c: CameraCoverage;
      if (!s) c = unknownCoverage(cam, camStates[i] ?? null);
      else {
        const host = cam.mount === 'aircraft' && cam.aircraftId ? (aircraft.find((a) => a.aircraftId === cam.aircraftId) ?? null) : null;
        c = evalCamera(cam, camStates[i] ?? null, targetOf(s, heightFt), aircraftId, host, span, minFill, opts?.sight);
      }
      if (c.state === 'in_frame') {
        anyIn = true;
        fills[i]![k] = c.fillPct ?? NaN;
      }
      const segs = runs[i]!.segments;
      let seg = segs[segs.length - 1];
      if (!seg || seg.state !== c.state || seg.to !== t) {
        seg = { from: t, to: end, state: c.state, bestFillPct: null, bestT: null, minRangeM: null };
        segs.push(seg);
      } else seg.to = end;
      if (c.fillPct != null && (seg.bestFillPct == null || c.fillPct > seg.bestFillPct)) {
        seg.bestFillPct = c.fillPct;
        seg.bestT = t;
      }
      if (c.rangeM != null && (seg.minRangeM == null || c.rangeM < seg.minRangeM)) seg.minRangeM = c.rangeM;
    }
    times[k] = t;
    used = k + 1;
    extend(unknown, t, !air && (!s || phaseForState(raw!, obs, { airports: record.airports }).phase === 'UNKNOWN'), end);
    extend(inferredAirborne, t, air && !s, end);
    extend(airborne, t, air, end);
    extend(nobody, t, air && !anyIn, end);
    if (end >= to) break;
  }
  const win = Math.max(1, Math.round(PEAK_WINDOW_SEC / step));
  for (let i = 0; i < runs.length; i++) {
    const f = fills[i]!;
    for (let k = 0; k < used; k++) {
      const v = f[k]!;
      if (Number.isNaN(v)) continue;
      let peak = true;
      for (let j = Math.max(0, k - win); j <= Math.min(used - 1, k + win) && peak; j++) {
        const w = f[j]!;
        if (j !== k && !Number.isNaN(w) && (w > v || (w === v && j < k))) peak = false;
      }
      if (peak) runs[i]!.peaks.push({ t: times[k]!, fillPct: v });
    }
  }
  const out: CoverageSampling = { aircraftId, from, to, stepSec: step, cameras: runs, airborne, nobody, unknown, inferredAirborne };
  if (!byKey) timelineCache.set(record, (byKey = new Map()));
  byKey.set(key, out);
  return out;
}

/** Contract shape: per-camera run-length segments for Replay lanes (see coverageSampling). */
export function coverageTimeline(record: MissionRecord, aircraftId: Id, from: EpochMs, to: EpochMs, stepSec = COVERAGE_STEP_SEC, opts?: Omit<CoverageTimelineOptions, 'stepSec'>): CameraTimeline[] {
  return coverageSampling(record, aircraftId, from, to, { ...opts, stepSec }).cameras;
}

// ───────────── summary (Replay debrief) ─────────────

export interface CameraCoverageSummary {
  cameraId: Id;
  inFrameSec: number;
  /** Closest range while in frame, else overall (m). */
  closestRangeM: number | null;
  /** Largest fill while in frame. */
  best: { t: EpochMs; fillPct: number } | null;
  /** In-frame stretches (for the editor: which camera, when). */
  inFrame: Span[];
  /** Where an in-frame stretch ended while the aircraft was airborne, and why. */
  lost: Array<{ t: EpochMs; reason: Exclude<CoverageState, 'in_frame'> }>;
}

export interface CoverageSummary {
  aircraftId: Id;
  airborneSec: number;
  unknownSec: number;
  inferredAirborneSec: number;
  /** Airborne seconds with at least one camera in frame. */
  coveredSec: number;
  /** coveredSec / airborneSec × 100, null when never airborne. */
  coveredPct: number | null;
  cameras: CameraCoverageSummary[];
  /** Airborne stretches nobody had (≥ minGapSec). */
  gaps: Span[];
  airborne: Span[];
}

const overlapSec = (a: Span, list: readonly Span[]) => {
  let s = 0;
  for (const b of list) s += Math.max(0, Math.min(a.to, b.to) - Math.max(a.from, b.from));
  return s / 1000;
};
const inAny = (t: EpochMs, list: readonly Span[]) => list.some((s) => t >= s.from && t <= s.to);

/** Per-camera minutes in frame, closest range, best moment, where coverage was lost; airborne % covered and gaps. */
export function coverageSummary(sampling: CoverageSampling, opts?: { minGapSec?: number }): CoverageSummary {
  const minGap = (opts?.minGapSec ?? 4) * 1000;
  const airborneSec = sampling.airborne.reduce((s, a) => s + (a.to - a.from) / 1000, 0);
  const nobodySec = sampling.nobody.reduce((s, a) => s + (a.to - a.from) / 1000, 0);
  const coveredSec = Math.max(0, airborneSec - nobodySec);
  const cameras = sampling.cameras.map<CameraCoverageSummary>((ct) => {
    let inFrameSec = 0;
    let closest: number | null = null;
    let closestAny: number | null = null;
    let best: CameraCoverageSummary['best'] = null;
    const inFrame: Span[] = [];
    const lost: CameraCoverageSummary['lost'] = [];
    ct.segments.forEach((seg, i) => {
      if (seg.minRangeM != null && (closestAny == null || seg.minRangeM < closestAny)) closestAny = seg.minRangeM;
      if (seg.state !== 'in_frame') return;
      inFrameSec += overlapSec(seg, sampling.airborne);
      for (const flight of sampling.airborne) {
        const from = Math.max(seg.from, flight.from), to = Math.min(seg.to, flight.to);
        if (to > from) inFrame.push({ from, to });
      }
      if (seg.minRangeM != null && (closest == null || seg.minRangeM < closest)) closest = seg.minRangeM;
      if (seg.bestFillPct != null && seg.bestT != null && (!best || seg.bestFillPct > best.fillPct)) best = { t: seg.bestT, fillPct: seg.bestFillPct };
      const next = ct.segments[i + 1];
      if (next && next.state !== 'in_frame' && inAny(seg.to, sampling.airborne)) lost.push({ t: seg.to, reason: next.state });
    });
    return { cameraId: ct.cameraId, inFrameSec, closestRangeM: closest ?? closestAny, best, inFrame, lost };
  });
  // merge in-frame stretches separated by a single sample (ADS-B jitter at the frame edge)
  for (const c of cameras) c.inFrame = mergeSpans(c.inFrame, sampling.stepSec * 1000);
  const gaps = sampling.nobody.filter((g) => g.to - g.from >= minGap);
  return {
    aircraftId: sampling.aircraftId,
    airborneSec,
    inferredAirborneSec: sampling.inferredAirborne.reduce((n, g) => n + (g.to - g.from) / 1000, 0),
    unknownSec: sampling.unknown.reduce((n, g) => n + (g.to - g.from) / 1000, 0),
    coveredSec,
    coveredPct: airborneSec > 0 ? (coveredSec / airborneSec) * 100 : null,
    cameras,
    gaps,
    airborne: sampling.airborne,
  };
}

function mergeSpans(spans: Span[], tolMs: number): Span[] {
  const out: Span[] = [];
  for (const s of spans) {
    const last = out[out.length - 1];
    if (last && s.from - last.to <= tolMs) last.to = Math.max(last.to, s.to);
    else out.push({ ...s });
  }
  return out;
}

/** Coverage state of one camera at t from its segments (binary search); null outside the sampled range. */
export function coverageStateAt(segments: readonly CoverageSegment[], t: EpochMs): CoverageState | null {
  let lo = 0;
  let hi = segments.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    const s = segments[mid]!;
    if (t < s.from) hi = mid - 1;
    else if (t >= s.to && mid < segments.length - 1) lo = mid + 1;
    else return t <= s.to ? s.state : null;
  }
  return null;
}

/** Seconds a camera spent in frame overlapping a span (helper for UI summaries). */
export function inFrameWithin(summary: CameraCoverageSummary, span: Span): number {
  return overlapSec(span, summary.inFrame);
}
